import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, superadminApi } from '../lib/api';
import type { SuperAdminTenant } from '../lib/api';
import PromoteToCustomerModal from '../components/PromoteToCustomerModal';
import type { CurrentUser } from '../types';
import AppShell from '../components/AppShell';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  surface:  '#ffffff',
  surface2: '#f1f5f9',
  canvas:   '#f8fafc',
  primary:  '#0ea5e9',
  green:    '#10b981',
  red:      '#ef4444',
  textPrimary:   '#0f172a',
  textSecondary: '#475569',
  textMuted:     '#94a3b8',
  border:   '#e2e8f0',
} as const;

const SANS  = "'Roboto', system-ui, sans-serif";
const MONO  = "'Courier New', monospace";

// ── Onboarding stage config ───────────────────────────────────────────────────
const STAGE_CONFIG: Record<number, { label: string; color: string; bg: string; border: string }> = {
  1: { label: 'Excel Sent',    color: '#475569', bg: '#f1f5f9',  border: '#e2e8f0' },
  2: { label: 'Data Uploaded', color: '#92400e', bg: '#fef3c7',  border: 'rgba(245,158,11,0.4)' },
  3: { label: 'Benchmarked',   color: '#0369a1', bg: '#e0f2fe',  border: 'rgba(14,165,233,0.4)' },
  4: { label: 'Proposal Sent', color: '#92400e', bg: '#fef3c7',  border: 'rgba(245,158,11,0.4)' },
  5: { label: 'PO Received',   color: '#92400e', bg: '#fef3c7',  border: 'rgba(245,158,11,0.4)' },
  6: { label: 'Activating',    color: '#0369a1', bg: '#e0f2fe',  border: 'rgba(14,165,233,0.4)' },
  7: { label: 'Live',          color: '#065f46', bg: '#d1fae5',  border: 'rgba(16,185,129,0.4)' },
};

// ── Auto-fill maps ────────────────────────────────────────────────────────────
const COUNTRY_TO_REGION: Record<string, string> = {
  'United States': 'US',
  'United Kingdom': 'UK',
  'Germany': 'EU',
  'France': 'EU',
  'Netherlands': 'EU',
  'Spain': 'EU',
  'India': 'IN',
  'Australia': 'ANZ',
  'New Zealand': 'ANZ',
};

const REGION_TO_CURRENCY: Record<string, string> = {
  US: 'USD', UK: 'GBP', EU: 'EUR', IN: 'INR', ANZ: 'AUD',
};

// ── Onboarding form state ──────────────────────────────────────────────────────
interface OnboardForm {
  name: string;
  emailDomain: string;
  portalAdminEmail: string;
  hqCountry: string;
  region: string;
  displayCurrency: string;
  estimatedSpend: string;
  source: string;
  notes: string;
}

const EMPTY_FORM: OnboardForm = {
  name: '', emailDomain: '', portalAdminEmail: '',
  hqCountry: '', region: '', displayCurrency: '',
  estimatedSpend: '', source: '', notes: '',
};

type ModalState = {
  tenant: SuperAdminTenant;
  step: 1 | 2;
  slugInput: string;
  loading: boolean;
  error: string | null;
} | null;

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function SuperAdminPage() {
  const navigate   = useNavigate();
  const [user, setUser]         = useState<CurrentUser | null>(null);
  const [tenants, setTenants]   = useState<SuperAdminTenant[]>([]);
  const [loading, setLoading]   = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportedIds, setExportedIds] = useState<Set<string>>(new Set());
  const [modal, setModal]       = useState<ModalState>(null);
  const slugInputRef            = useRef<HTMLInputElement>(null);

  const [formOpen, setFormOpen]         = useState(false);
  const [form, setForm]                 = useState<OnboardForm>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('sl_onboard_draft') ?? 'null');
      return saved ? { ...EMPTY_FORM, ...saved } : EMPTY_FORM;
    } catch { return EMPTY_FORM; }
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [draftSaved, setDraftSaved]     = useState(false);
  const [toast, setToast]               = useState<string | null>(null);

  useEffect(() => {
    authApi.me()
      .then((u) => {
        if (u.role !== 'super_admin') {
          navigate('/dashboard', { replace: true });
          return;
        }
        setUser(u);
        return superadminApi.tenants();
      })
      .then((list) => { if (list) setTenants(list); })
      .catch(() => navigate('/login', { replace: true }))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (modal?.step === 2) {
      setTimeout(() => slugInputRef.current?.focus(), 60);
    }
  }, [modal?.step]);

  if (!user) return null;

  async function handleExport(tenant: SuperAdminTenant) {
    setExportingId(tenant.id);
    setPageError(null);
    try {
      await superadminApi.exportTenant(tenant.id, tenant.slug);
      setExportedIds((prev) => new Set([...prev, tenant.id]));
    } catch (err) {
      setPageError(`Export failed for ${tenant.name}: ${(err as Error).message}`);
    } finally {
      setExportingId(null);
    }
  }

  function openOffboardModal(tenant: SuperAdminTenant) {
    setModal({ tenant, step: 1, slugInput: '', loading: false, error: null });
  }

  function closeModal() {
    if (modal?.loading) return;
    setModal(null);
  }

  function advanceToStep2() {
    setModal((m) => m ? { ...m, step: 2 } : m);
  }

  async function confirmOffboard() {
    if (!modal || modal.slugInput !== modal.tenant.slug) return;
    setModal((m) => m ? { ...m, loading: true, error: null } : m);
    try {
      await superadminApi.offboard(modal.tenant.id);
      setTenants((prev) =>
        prev.map((t) =>
          t.id === modal.tenant.id
            ? { ...t, isActive: false, offboardedAt: new Date().toISOString() }
            : t,
        ),
      );
      setModal(null);
    } catch (err) {
      setModal((m) =>
        m ? { ...m, loading: false, error: (err as Error).message } : m,
      );
    }
  }

  const setField = (key: keyof OnboardForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => setForm((f) => ({ ...f, [key]: e.target.value }));

  function handleCountryChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const country = e.target.value;
    const region = COUNTRY_TO_REGION[country] ?? '';
    const displayCurrency = REGION_TO_CURRENCY[region] ?? '';
    setForm((f) => ({ ...f, hqCountry: country, region, displayCurrency }));
  }

  function handleRegionChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const region = e.target.value;
    const displayCurrency = REGION_TO_CURRENCY[region] ?? '';
    setForm((f) => ({ ...f, region, displayCurrency }));
  }

  function saveDraft() {
    localStorage.setItem('sl_onboard_draft', JSON.stringify(form));
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2500);
  }

  async function handleOnboard() {
    if (!form.name.trim() || !form.emailDomain.trim() || !form.portalAdminEmail.trim()) {
      setPageError('Company name, email domain, and portal admin email are required.');
      return;
    }
    if (!form.hqCountry || !form.region || !form.displayCurrency) {
      setPageError('HQ Country, Region, and Reporting Currency are required.');
      return;
    }
    setFormSubmitting(true);
    setPageError(null);
    try {
      const result = await superadminApi.onboard({
        name:             form.name.trim(),
        emailDomain:      form.emailDomain.trim().toLowerCase(),
        portalAdminEmail: form.portalAdminEmail.trim().toLowerCase(),
        displayCurrency:  form.displayCurrency,
        region:           form.region,
        hqCountry:        form.hqCountry,
        estimatedSpend:   form.estimatedSpend || undefined,
        source:           form.source || undefined,
        notes:            form.notes.trim() || undefined,
      });
      setTenants((prev) => [result.tenant, ...prev]);
      setToast(`✅ ${result.tenant.name} created with ${form.displayCurrency} as reporting currency. Send Part 1 Excel to ${form.portalAdminEmail} to begin.`);
      setForm(EMPTY_FORM);
      localStorage.removeItem('sl_onboard_draft');
      setFormOpen(false);
      setTimeout(() => setToast(null), 7000);
      navigate(`/superadmin/tenants/${result.tenant.id}/onboarding`);
    } catch (err) {
      setPageError((err as Error).message);
    } finally {
      setFormSubmitting(false);
    }
  }

  return (
    <AppShell user={user} title="SuperAdmin" subtitle="Client Management">
      <style>{`
        @keyframes sl-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.75)} }
        .sl-live-dot { animation: sl-pulse 1.6s ease-in-out infinite; }
        .sa-tr.scrubbed { opacity: 0.5; }
        .sa-tr.scrubbed:hover { opacity: 0.7; }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          background: '#d1fae5', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          fontSize: 13, color: '#065f46',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{toast}</span>
          <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', color: '#065f46', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Global error */}
      {pageError && (
        <div style={{
          background: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          fontSize: 13, color: C.red,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{pageError}</span>
          <button onClick={() => setPageError(null)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* ── Onboard New Client panel ──────────────────────────────────────────── */}
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
        overflow: 'hidden', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <div
          style={{
            padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', borderBottom: formOpen ? `1px solid ${C.border}` : 'none',
          }}
          onClick={() => setFormOpen((o) => !o)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 15, color: C.primary }}>◈</span>
            <div>
              <span style={{ fontSize: 13, fontWeight: 400, color: C.textPrimary, letterSpacing: '-0.2px' }}>Onboard New Client</span>
              <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 10 }}>Create tenant + portal admin in one step</span>
            </div>
          </div>
          <span style={{ fontSize: 18, color: C.textMuted, lineHeight: 1, transform: formOpen ? 'rotate(45deg)' : 'none', transition: 'transform 0.15s' }}>+</span>
        </div>

        {formOpen && (
          <div style={{ padding: '20px 20px 16px' }}>

            {/* Row 1 — Company identity */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Company Name *</label>
                <input className="sa-input" value={form.name} onChange={setField('name')}
                  placeholder="e.g. Acme Corp" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Email Domain *</label>
                <input className="sa-input" value={form.emailDomain} onChange={setField('emailDomain')}
                  placeholder="acme.com" style={inputStyle} />
                <span style={helperStyle}>Users with this domain auto-assigned to tenant</span>
              </div>
            </div>

            {/* Row 2 — Geography + Currency (NEW) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 16px', marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>HQ Country *</label>
                <select className="sa-input" value={form.hqCountry} onChange={handleCountryChange} style={inputStyle}>
                  <option value="">Select country…</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Spain">Spain</option>
                  <option value="India">India</option>
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Other">Other</option>
                </select>
                <span style={helperStyle}>Select 'Other' if not listed</span>
              </div>
              <div>
                <label style={labelStyle}>Region *</label>
                <select className="sa-input" value={form.region} onChange={handleRegionChange} style={inputStyle}>
                  <option value="">Select region…</option>
                  <option value="US">US</option>
                  <option value="UK">UK</option>
                  <option value="EU">EU</option>
                  <option value="IN">IN</option>
                  <option value="ANZ">ANZ</option>
                </select>
                <span style={helperStyle}>Auto-suggested from country — adjust if needed</span>
              </div>
              <div>
                <label style={labelStyle}>Reporting Currency *</label>
                <select className="sa-input" value={form.displayCurrency} onChange={setField('displayCurrency')} style={inputStyle}>
                  <option value="">Select currency…</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                  <option value="INR">INR</option>
                  <option value="AUD">AUD</option>
                </select>
                <span style={helperStyle}>Dashboard will display amounts in this currency</span>
              </div>
            </div>

            {/* Row 3 — Contact + spend */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Portal Admin Email *</label>
                <input className="sa-input" type="email" value={form.portalAdminEmail} onChange={setField('portalAdminEmail')}
                  placeholder="cfo@acme.com" style={inputStyle} />
                <span style={helperStyle}>This person becomes the Procurement Head</span>
              </div>
              <div>
                <label style={labelStyle}>Estimated Annual Tech Spend</label>
                <select className="sa-input" value={form.estimatedSpend} onChange={setField('estimatedSpend')} style={inputStyle}>
                  <option value="">Select range…</option>
                  <option value="$50K–$250K">$50K–$250K</option>
                  <option value="$250K–$1M">$250K–$1M</option>
                  <option value="$1M–$5M">$1M–$5M</option>
                  <option value="$5M–$20M">$5M–$20M</option>
                  <option value="$20M+">$20M+</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Lead Source</label>
                <select className="sa-input" value={form.source} onChange={setField('source')} style={inputStyle}>
                  <option value="">Select source…</option>
                  <option value="Partner referral">Partner referral</option>
                  <option value="Direct">Direct</option>
                  <option value="Inbound">Inbound</option>
                  <option value="Conference">Conference</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Notes (optional)</label>
              <textarea className="sa-input" value={form.notes} onChange={setField('notes')}
                rows={3} placeholder="Any context about this client, deal specifics, or next steps…"
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="sa-btn sa-btn-ghost"
                onClick={saveDraft}
                style={{
                  padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 400,
                  cursor: 'pointer', border: `1px solid ${C.border}`,
                  background: 'transparent', color: draftSaved ? '#10b981' : C.textSecondary,
                  fontFamily: SANS, transition: 'all 0.15s',
                }}
              >
                {draftSaved ? '✓ Draft saved' : 'Save Draft'}
              </button>
              <button
                className="sa-btn"
                onClick={handleOnboard}
                disabled={formSubmitting}
                style={{
                  padding: '7px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 400,
                  cursor: formSubmitting ? 'not-allowed' : 'pointer',
                  border: 'none', background: formSubmitting ? '#7dd3fc' : C.primary,
                  color: '#ffffff', fontFamily: SANS, transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 1px 3px rgba(14,165,233,0.3)',
                }}
              >
                {formSubmitting ? <><Spinner /> Creating…</> : 'Create Client →'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tenant table */}
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
        overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, fontWeight: 400, color: C.textPrimary, letterSpacing: '-0.2px' }}>
            All Clients
            <span style={{ marginLeft: 8, fontSize: 12, color: C.textMuted, fontWeight: 300 }}>
              {loading ? '…' : `${tenants.length} total`}
            </span>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: C.textMuted, fontSize: 13 }}>Loading clients…</div>
        ) : tenants.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: C.textMuted, fontSize: 13 }}>No clients found.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.canvas }}>
                {['Client', 'Domain', 'Stage', 'Status', 'Created', 'Offboarded', 'Promote', 'Export', 'Offboard'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <TenantRow
                  key={tenant.id}
                  tenant={tenant}
                  isExporting={exportingId === tenant.id}
                  isExported={exportedIds.has(tenant.id)}
                  onExport={() => handleExport(tenant)}
                  onOffboard={() => openOffboardModal(tenant)}
                  onRowClick={() => navigate(`/superadmin/tenants/${tenant.id}/onboarding`)}
                  onStageChange={(newStage) =>
                    setTenants((prev) =>
                      prev.map((t) => t.id === tenant.id ? { ...t, onboardingStage: newStage } : t),
                    )
                  }
                  onToast={(msg) => { setToast(msg); setTimeout(() => setToast(null), 6000); }}
                  onError={(msg) => setPageError(msg)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Offboard Modal */}
      {modal && (
        <OffboardModal
          modal={modal}
          slugInputRef={slugInputRef}
          onSlugChange={(v) => setModal((m) => m ? { ...m, slugInput: v } : m)}
          onClose={closeModal}
          onContinue={advanceToStep2}
          onBack={() => setModal((m) => m ? { ...m, step: 1, slugInput: '', error: null } : m)}
          onConfirm={confirmOffboard}
        />
      )}
    </AppShell>
  );
}

// ── TenantRow ──────────────────────────────────────────────────────────────────
function TenantRow({
  tenant, isExporting, isExported, onExport, onOffboard, onRowClick, onStageChange, onToast, onError,
}: {
  tenant: SuperAdminTenant;
  isExporting: boolean;
  isExported: boolean;
  onExport: () => void;
  onOffboard: () => void;
  onRowClick: () => void;
  onStageChange: (newStage: number) => void;
  onToast: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const offboarded = !!tenant.offboardedAt;
  const scrubbed   = !tenant.isActive && !tenant.offboardedAt;
  const [stageEditing, setStageEditing] = useState(false);
  const [stageLoading, setStageLoading] = useState(false);
  const [hoverStage, setHoverStage] = useState(false);
  const [rowHover, setRowHover] = useState(false);
  const [showPromote, setShowPromote] = useState(false);

  const currentStage = tenant.onboardingStage ?? 1;

  async function handleStageSelect(e: React.ChangeEvent<HTMLSelectElement>) {
    const newStage = Number(e.target.value);
    setStageEditing(false);
    if (newStage === currentStage) return;
    setStageLoading(true);
    try {
      await superadminApi.updateStage(tenant.id, newStage);
      onStageChange(newStage);
      onToast(`Stage updated to "${STAGE_CONFIG[newStage]?.label}" for ${tenant.name}`);
    } catch (err) {
      onError(`Failed to update stage: ${(err as Error).message}`);
    } finally {
      setStageLoading(false);
    }
  }

  async function handleAdvanceStage() {
    if (currentStage >= 7 || stageLoading) return;
    const newStage = currentStage + 1;
    setStageLoading(true);
    try {
      await superadminApi.updateStage(tenant.id, newStage);
      onStageChange(newStage);
      onToast(`Stage advanced to "${STAGE_CONFIG[newStage]?.label}" for ${tenant.name}`);
    } catch (err) {
      onError(`Failed to advance stage: ${(err as Error).message}`);
    } finally {
      setStageLoading(false);
    }
  }

  return (
    <tr
      className={`sa-tr${offboarded ? ' offboarded' : ''}${scrubbed ? ' scrubbed' : ''}`}
      onClick={onRowClick}
      onMouseEnter={() => setRowHover(true)}
      onMouseLeave={() => setRowHover(false)}
      style={{
        cursor: 'pointer',
        background: rowHover ? C.canvas : 'transparent',
        transition: 'background 0.12s',
      }}
    >
      <td style={tdStyle}>
        <div style={{ fontSize: 13.5, fontWeight: 400, color: C.textPrimary }}>{tenant.name}</div>
        <div style={{ fontSize: 11, color: C.textMuted, fontFamily: MONO, marginTop: 2 }}>{tenant.slug}</div>
      </td>
      <td style={tdStyle}>
        <span style={{ fontFamily: MONO, fontSize: 12, color: C.textMuted }}>{tenant.emailDomain ?? '—'}</span>
      </td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {stageEditing ? (
            <>
              <select
                autoFocus
                value={currentStage}
                onChange={handleStageSelect}
                onBlur={() => setStageEditing(false)}
                onClick={(e) => e.stopPropagation()}
                disabled={stageLoading}
                style={{
                  border: '1px solid #0ea5e9',
                  boxShadow: '0 0 0 3px rgba(14,165,233,0.15)',
                  borderRadius: 6, padding: '3px 6px',
                  fontSize: 12, color: C.textPrimary,
                  background: '#fff', cursor: 'pointer',
                  fontFamily: SANS, outline: 'none',
                }}
              >
                {Object.entries(STAGE_CONFIG).map(([s, cfg]) => (
                  <option key={s} value={s}>{s}. {cfg.label}</option>
                ))}
              </select>
              {stageLoading && <MiniSpinner />}
            </>
          ) : (
            <>
              <span
                onMouseEnter={() => setHoverStage(true)}
                onMouseLeave={() => setHoverStage(false)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!offboarded && !stageLoading) setStageEditing(true);
                }}
                title={offboarded ? undefined : 'Click to change stage'}
                style={{
                  cursor: offboarded ? 'default' : 'pointer',
                  borderRadius: 20,
                  outline: hoverStage && !offboarded ? '2px solid #e2e8f0' : '2px solid transparent',
                  outlineOffset: 2,
                  transition: 'outline 0.1s',
                  display: 'inline-flex',
                }}
              >
                <StageBadge stage={currentStage} />
              </span>
              {!offboarded && currentStage < 7 && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdvanceStage(); }}
                  disabled={stageLoading}
                  title={`Advance to ${currentStage + 1}: ${STAGE_CONFIG[currentStage + 1]?.label}`}
                  style={{
                    background: 'none', border: 'none', padding: '2px 3px',
                    cursor: stageLoading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center',
                    color: stageLoading ? C.textMuted : '#64748b',
                    borderRadius: 4, opacity: stageLoading ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => { if (!stageLoading) e.currentTarget.style.color = '#0ea5e9'; }}
                  onMouseLeave={(e) => { if (!stageLoading) e.currentTarget.style.color = '#64748b'; }}
                >
                  {stageLoading ? <MiniSpinner /> : <ChevronRightSvg />}
                </button>
              )}
            </>
          )}
        </div>
      </td>
      <td style={tdStyle}>
        {offboarded ? (
          <span style={{ ...badgeBase, background: '#f1f5f9', color: '#475569' }}>
            Offboarded
          </span>
        ) : scrubbed ? (
          <span style={{ ...badgeBase, background: '#f1f5f9', color: '#94a3b8' }}>
            Scrubbed
          </span>
        ) : (
          <span style={{ ...badgeBase, background: '#d1fae5', color: '#065f46' }}>
            ● Active
          </span>
        )}
      </td>
      <td style={{ ...tdStyle, color: C.textMuted, fontSize: 12 }}>{fmtDate(tenant.createdAt)}</td>
      <td style={{ ...tdStyle, color: C.textMuted, fontSize: 12 }}>{fmtDate(tenant.offboardedAt)}</td>

      <td style={tdStyle}>
        {!offboarded && !scrubbed && tenant.customerStatus === 'prospect' ? (
          <button
            className="sa-btn"
            onClick={(e) => { e.stopPropagation(); setShowPromote(true); }}
            title="Create engagement record + invite portal admin"
            style={{
              padding: '6px 12px', borderRadius: 7, fontSize: 12, fontWeight: 400,
              cursor: 'pointer', border: '1px solid rgba(14,165,233,0.4)',
              background: '#e0f2fe', color: '#0369a1',
              fontFamily: SANS, transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}
          >
            ★ Promote
          </button>
        ) : tenant.customerStatus === 'customer' ? (
          <span style={{ ...badgeBase, background: '#dbeafe', color: '#1e40af' }}>Customer</span>
        ) : (
          <span style={{ fontSize: 12, color: C.textMuted }}>—</span>
        )}
      </td>

      <td style={tdStyle}>
        <button
          className="sa-btn"
          disabled={isExporting || offboarded}
          onClick={(e) => { e.stopPropagation(); onExport(); }}
          style={{
            padding: '6px 12px', borderRadius: 7, fontSize: 12, fontWeight: 400,
            cursor: isExporting || offboarded ? 'not-allowed' : 'pointer',
            border: `1px solid ${C.border}`, background: 'transparent',
            color: isExported ? '#10b981' : C.textSecondary,
            fontFamily: SANS, transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}
          title={offboarded ? 'Tenant already offboarded' : undefined}
        >
          {isExporting ? <Spinner /> : isExported ? '✓ Exported' : '⬇ Export Data'}
        </button>
      </td>

      <td style={tdStyle}>
        {offboarded ? (
          <span style={{ fontSize: 12, color: C.textMuted }}>—</span>
        ) : (
          <button
            className="sa-btn"
            disabled={!isExported}
            onClick={(e) => { e.stopPropagation(); onOffboard(); }}
            title={!isExported ? 'Export data first before offboarding' : undefined}
            style={{
              padding: '6px 12px', borderRadius: 7, fontSize: 12, fontWeight: 400,
              cursor: isExported ? 'pointer' : 'not-allowed',
              border: `1px solid ${isExported ? 'rgba(239,68,68,0.4)' : C.border}`,
              background: isExported ? '#fee2e2' : 'transparent',
              color: isExported ? C.red : C.textMuted,
              fontFamily: SANS, transition: 'all 0.15s',
            }}
          >
            Offboard
          </button>
        )}
      </td>

      {showPromote && (
        <td style={{ padding: 0, border: 'none' }} onClick={(e) => e.stopPropagation()}>
          <PromoteToCustomerModal
            tenantId={tenant.id}
            tenantName={tenant.name}
            prospectContactEmail={null}
            onClose={() => setShowPromote(false)}
            onSuccess={(msg: string) => { onToast(msg); onStageChange(currentStage); }}
          />
        </td>
      )}
    </tr>
  );
}

// ── OffboardModal ──────────────────────────────────────────────────────────────
function OffboardModal({
  modal, slugInputRef, onSlugChange, onClose, onContinue, onBack, onConfirm,
}: {
  modal: NonNullable<ModalState>;
  slugInputRef: React.RefObject<HTMLInputElement>;
  onSlugChange: (v: string) => void;
  onClose: () => void;
  onContinue: () => void;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const slugMatch = modal.slugInput === modal.tenant.slug;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 14, width: 480, maxWidth: '100%',
        boxShadow: '0 8px 24px rgba(0,0,0,0.10), 0 4px 8px rgba(0,0,0,0.04)',
      }}>
        {/* Modal header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: SANS, fontSize: 17, fontWeight: 300, color: C.textPrimary, letterSpacing: '-0.3px' }}>
              {modal.step === 1 ? `Offboard ${modal.tenant.name}?` : 'Final confirmation'}
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3 }}>
              Step {modal.step} of 2
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={modal.loading}
            style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', color: C.textMuted, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >×</button>
        </div>

        {modal.step === 1 ? (
          <>
            <div style={{ padding: '20px 24px' }}>
              <div style={{
                background: '#fee2e2', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 20,
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>⚠</span>
                <span style={{ fontSize: 12.5, color: C.red, lineHeight: 1.5 }}>
                  <strong>This cannot be undone.</strong> Once confirmed, all personal data will be anonymised and access revoked immediately.
                </span>
              </div>

              <div style={{ fontSize: 12, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
                What will happen
              </div>
              {[
                { icon: '🗑', label: 'WorkOS users deleted from the identity provider' },
                { icon: '🔒', label: 'All personal data anonymised (names, email addresses)' },
                { icon: '🚫', label: 'Tenant access revoked immediately across all sessions' },
                { icon: '📋', label: 'Business records retained for compliance (PRs, POs, approvals)' },
                { icon: '📧', label: 'Notification email sent to tenant admins (CFO, Procurement Head)' },
              ].map(({ icon, label }) => (
                <div key={label} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 14, flexShrink: 0, width: 20, textAlign: 'center' }}>{icon}</span>
                  <span style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.5 }}>{label}</span>
                </div>
              ))}
            </div>

            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="sa-btn sa-btn-ghost"
                onClick={onClose}
                style={{ padding: '7px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 400, cursor: 'pointer', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontFamily: SANS }}
              >Cancel</button>
              <button
                className="sa-btn"
                onClick={onContinue}
                style={{ padding: '7px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 400, cursor: 'pointer', border: '1px solid rgba(239,68,68,0.35)', background: '#fee2e2', color: C.red, fontFamily: SANS }}
              >I understand, continue →</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: 13, color: C.textSecondary, marginTop: 0, marginBottom: 16, lineHeight: 1.6 }}>
                Type the client slug to confirm you want to permanently offboard this tenant.
              </p>

              <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: C.textMuted, whiteSpace: 'nowrap' }}>Slug:</span>
                <span style={{ fontFamily: MONO, fontSize: 14, color: C.textPrimary, letterSpacing: '0.5px' }}>
                  {modal.tenant.slug}
                </span>
              </div>

              <input
                ref={slugInputRef}
                className="sa-input"
                type="text"
                value={modal.slugInput}
                onChange={(e) => onSlugChange(e.target.value)}
                placeholder={modal.tenant.slug}
                disabled={modal.loading}
                onKeyDown={(e) => { if (e.key === 'Enter' && slugMatch) onConfirm(); }}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: '#ffffff', border: `1px solid ${slugMatch && modal.slugInput ? 'rgba(239,68,68,0.5)' : C.border}`,
                  borderRadius: 8, padding: '10px 12px', fontFamily: MONO,
                  fontSize: 13.5, color: C.textPrimary, transition: 'border-color 0.2s, box-shadow 0.2s',
                  outline: 'none',
                }}
                autoComplete="off"
                spellCheck={false}
              />

              {modal.error && (
                <div style={{ marginTop: 12, fontSize: 12.5, color: C.red, background: '#fee2e2', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 7, padding: '8px 12px' }}>
                  {modal.error}
                </div>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                className="sa-btn sa-btn-ghost"
                onClick={onBack}
                disabled={modal.loading}
                style={{ padding: '7px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 400, cursor: 'pointer', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontFamily: SANS }}
              >← Back</button>

              <button
                className="sa-btn"
                onClick={onConfirm}
                disabled={!slugMatch || modal.loading}
                style={{
                  padding: '7px 20px', borderRadius: 8, fontSize: 12.5, fontWeight: 400,
                  cursor: slugMatch && !modal.loading ? 'pointer' : 'not-allowed',
                  border: 'none', fontFamily: SANS, transition: 'all 0.15s',
                  background: slugMatch && !modal.loading ? C.red : '#fca5a5',
                  color: slugMatch && !modal.loading ? '#fff' : 'rgba(255,255,255,0.6)',
                  minWidth: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {modal.loading ? <><Spinner /> Offboarding…</> : 'Confirm Offboard'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Spinner ────────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <span style={{
      display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: 'rgba(255,255,255,0.9)',
      animation: 'sa-spin 0.6s linear infinite',
    }} />
  );
}

function MiniSpinner() {
  return (
    <span style={{
      display: 'inline-block', width: 11, height: 11, borderRadius: '50%',
      border: '1.5px solid rgba(100,116,139,0.25)',
      borderTopColor: '#64748b',
      animation: 'sa-spin 0.6s linear infinite', flexShrink: 0,
    }} />
  );
}

function ChevronRightSvg() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ── Shared style constants ─────────────────────────────────────────────────────
const thStyle: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 500, color: '#94a3b8', letterSpacing: '0.06em',
  textTransform: 'uppercase', padding: '10px 18px', textAlign: 'left',
  borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 18px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'middle',
};

const badgeBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
  borderRadius: 20, fontSize: 11, fontWeight: 400, whiteSpace: 'nowrap',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 500, color: '#64748b',
  letterSpacing: '0.06em', marginBottom: 5, textTransform: 'uppercase',
};

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box' as const,
  background: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 8, padding: '8px 11px',
  fontSize: 12.5, color: '#0f172a', fontFamily: SANS,
  outline: 'none', transition: 'border-color 0.15s',
  fontWeight: 300,
};

const helperStyle: React.CSSProperties = {
  display: 'block', fontSize: 10.5, color: '#94a3b8', marginTop: 4,
};

// ── StageBadge ─────────────────────────────────────────────────────────────────
function StageBadge({ stage }: { stage: number | null }) {
  const cfg = STAGE_CONFIG[stage ?? 1] ?? STAGE_CONFIG[1];
  const isLive = stage === 7;
  return (
    <span style={{
      ...badgeBase,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      gap: isLive ? 5 : 0,
    }}>
      {isLive && (
        <span className="sl-live-dot" style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
          background: cfg.color, flexShrink: 0,
        }} />
      )}
      {cfg.label}
    </span>
  );
}
